import type { AxiosResponse } from "axios";
import type { HttpClient } from "@/services/baseClient";
import { buildQueryString } from "@/services/baseClient";
import type {
  ApiResponse,
  TelemetryMetadataResponse,
  TelemetryDataResponse,
  TelemetryUploadRequest,
  TelemetryUploadResponse,
  TelemetryParseJobStatus,
  TelemetrySummaryResponse,
  SessionTelemetryFilesResponse,
  TelemetryFileDownloadInfo,
  TelemetryFile,
  TelemetryDataFilter,
  TelemetryDataPoint,
  Lap,
  TelemetryNote,
  TelemetryNoteWithSession,
  CreateTelemetryNoteRequest,
  UpdateTelemetryNoteRequest,
  TelemetryNotesResponse,
  TelemetryLapsResponse,
} from "@/types";

export const PARSE_POLL_INTERVAL_MS = 2000;
export const PARSE_POLL_MAX_ATTEMPTS = 150;

export class TelemetryParseJobCancelledError extends Error {
  constructor() {
    super("Telemetry parse job polling was cancelled");
    this.name = "TelemetryParseJobCancelledError";
  }
}

export class TelemetryParseJobTimeoutError extends Error {
  constructor() {
    super("Timed out waiting for the telemetry parse job to finish");
    this.name = "TelemetryParseJobTimeoutError";
  }
}

export interface PollTelemetryParseJobOptions {
  /** Checked before each attempt and before the timeout: when it returns true, polling stops with TelemetryParseJobCancelledError. */
  isCancelled?: () => boolean;
  intervalMs?: number;
  maxAttempts?: number;
}

export function createTelemetryApi({ client }: HttpClient) {
  return {
    async getTelemetryParseJobStatus(
      sessionId: string,
    ): Promise<TelemetryParseJobStatus> {
      const response: AxiosResponse<ApiResponse<TelemetryParseJobStatus>> =
        await client.get(`/telemetry-data/${sessionId}/parse-jobs/latest`);
      return response.data.data;
    },

    async pollTelemetryParseJob(
      sessionId: string,
      options: PollTelemetryParseJobOptions = {},
    ): Promise<TelemetryParseJobStatus> {
      const intervalMs = options.intervalMs ?? PARSE_POLL_INTERVAL_MS;
      const maxAttempts = options.maxAttempts ?? PARSE_POLL_MAX_ATTEMPTS;

      for (let attempt = 0; attempt < maxAttempts; attempt++) {
        if (options.isCancelled?.()) {
          throw new TelemetryParseJobCancelledError();
        }

        let job: TelemetryParseJobStatus | null = null;
        try {
          job = await this.getTelemetryParseJobStatus(sessionId);
        } catch {
          // Transient status-call failure: keep polling — the worker is
          // still retrying server-side, and the attempt ceiling bounds
          // the total wait.
        }

        if (
          job &&
          (job.status === "succeeded" ||
            job.status === "failed" ||
            job.status === "canceled")
        ) {
          return job;
        }

        await new Promise((resolve) => setTimeout(resolve, intervalMs));
      }

      if (options.isCancelled?.()) {
        throw new TelemetryParseJobCancelledError();
      }
      throw new TelemetryParseJobTimeoutError();
    },
    async getTelemetryMetadata(
      sessionId: string,
    ): Promise<TelemetryMetadataResponse> {
      const response: AxiosResponse<TelemetryMetadataResponse> =
        await client.get(`/telemetry-data/${sessionId}/metadata`);
      return response.data;
    },

    async getTelemetryFiles(
      sessionId: string,
    ): Promise<SessionTelemetryFilesResponse> {
      const response: AxiosResponse<
        ApiResponse<SessionTelemetryFilesResponse>
      > = await client.get(`/telemetry-data/${sessionId}/files`);
      return response.data.data;
    },

    async getTelemetryFileDetails(
      sessionId: string,
      fileId: string,
    ): Promise<TelemetryFile> {
      const response: AxiosResponse<ApiResponse<TelemetryFile>> =
        await client.get(`/telemetry-data/${sessionId}/files/${fileId}`);
      return response.data.data;
    },

    async getTelemetryFileDownloadUrl(
      fileId: string,
    ): Promise<TelemetryFileDownloadInfo> {
      const response: AxiosResponse<ApiResponse<TelemetryFileDownloadInfo>> =
        await client.get(`/telemetry-data/files/${fileId}/download`);
      return response.data.data;
    },

    async getTelemetryData(
      sessionId: string,
      filter?: TelemetryDataFilter,
    ): Promise<TelemetryDataResponse> {
      const queryParams = buildQueryString({
        ...(filter?.startTime !== undefined && {
          startTime: filter.startTime,
        }),
        ...(filter?.endTime !== undefined && { endTime: filter.endTime }),
        ...(filter?.channels &&
          filter.channels.length > 0 && {
            channels: filter.channels.join(","),
          }),
        ...(filter?.sampleRate !== undefined && {
          sampleRate: filter.sampleRate,
        }),
        ...(filter?.maxPoints !== undefined && {
          maxPoints: filter.maxPoints,
        }),
      });

      const url = `/telemetry-data/${sessionId}/data${queryParams}`;
      const response: AxiosResponse<TelemetryDataResponse> =
        await client.get(url);
      return response.data;
    },

    async getTelemetryLaps(sessionId: string): Promise<TelemetryLapsResponse> {
      const response: AxiosResponse<TelemetryLapsResponse> = await client.get(
        `/telemetry-data/${sessionId}/laps`,
      );
      return response.data;
    },

    async getTelemetryLapData(
      sessionId: string,
      lapNumber: number,
    ): Promise<{
      success: boolean;
      data: {
        lapNumber: number;
        startTime: number;
        endTime: number;
        lapTime: number;
        dataPointCount: number;
        sectorTimes?: number[];
        data: TelemetryDataPoint[];
      };
      timestamp: string;
    }> {
      const response: AxiosResponse<{
        success: boolean;
        data: {
          lapNumber: number;
          startTime: number;
          endTime: number;
          lapTime: number;
          dataPointCount: number;
          sectorTimes?: number[];
          data: TelemetryDataPoint[];
        };
        timestamp: string;
      }> = await client.get(
        `/telemetry-data/${sessionId}/laps/${lapNumber}`,
      );
      return response.data;
    },

    async uploadTelemetryData(
      sessionId: string,
      data: TelemetryUploadRequest,
    ): Promise<TelemetryUploadResponse> {
      const response: AxiosResponse<TelemetryUploadResponse> =
        await client.post(`/telemetry-data/${sessionId}/upload`, data);
      return response.data;
    },

    async uploadTelemetryFile(
      sessionId: string,
      file: File,
    ): Promise<TelemetryUploadResponse> {
      const data = new FormData();
      data.append("file", file);

      const response: AxiosResponse<TelemetryUploadResponse> =
        await client.post(`/telemetry-data/${sessionId}/upload`, data, {
          headers: {
            "Content-Type": "multipart/form-data",
          },
          // Axios timeouts are total-time (not idle), so a short cap would
          // abort healthy slow uploads (100MB max file on venue Wi-Fi can
          // take minutes). 10 min bounds a dead connection while tolerating
          // ~1 Mbps uplink across the full allowed size.
          timeout: 10 * 60 * 1000,
        });
      return response.data;
    },

    async getTelemetrySummary(
      sessionId: string,
    ): Promise<TelemetrySummaryResponse> {
      const response: AxiosResponse<TelemetrySummaryResponse> =
        await client.get(`/telemetry-data/${sessionId}/summary`);
      return response.data;
    },

    async deleteTelemetryData(
      sessionId: string,
    ): Promise<{ success: boolean }> {
      const response: AxiosResponse<{
        success: boolean;
        sessionId: string;
        existed: boolean;
      }> = await client.delete(`/telemetry-data/${sessionId}`);
      return { success: response.data.success };
    },

    async createLapsFromTelemetry(
      sessionId: string,
      options?: { overwrite?: boolean },
    ): Promise<{
      success: boolean;
      sessionId: string;
      created: number;
      errors: number;
      laps: Lap[];
      telemetryInfo: {
        totalLaps: number;
        beaconMarkers: number[];
        segmentTimes: string[];
        timeRange: { start: number; end: number };
      };
    }> {
      const response: AxiosResponse<{
        success: boolean;
        sessionId: string;
        created: number;
        errors: number;
        laps: Lap[];
        telemetryInfo: {
          totalLaps: number;
          beaconMarkers: number[];
          segmentTimes: string[];
          timeRange: { start: number; end: number };
        };
      }> = await client.post(
        `/telemetry-data/${sessionId}/create-laps`,
        options || {},
      );
      return response.data;
    },

    async getTelemetryNotes(params?: {
      sessionId?: string;
      userId?: string;
      search?: string;
      timeOffsetMin?: number;
      timeOffsetMax?: number;
      limit?: number;
      offset?: number;
      orderBy?: string;
      orderDirection?: string;
    }): Promise<TelemetryNotesResponse> {
      const response: AxiosResponse<
        ApiResponse<TelemetryNotesResponse>
      > = await client.get("/telemetry-notes", { params });
      return response.data.data;
    },

    async getTelemetryNotesBySession(
      sessionId: string,
    ): Promise<TelemetryNote[]> {
      const response: AxiosResponse<ApiResponse<TelemetryNote[]>> =
        await client.get(`/telemetry-notes/session/${sessionId}`);
      return response.data.data;
    },

    async getTelemetryNotesByUser(
      userId: string,
    ): Promise<TelemetryNoteWithSession[]> {
      const response: AxiosResponse<
        ApiResponse<TelemetryNoteWithSession[]>
      > = await client.get(`/telemetry-notes/user/${userId}`);
      return response.data.data;
    },

    async getTelemetryNotesInTimeRange(
      sessionId: string,
      start: number,
      end: number,
    ): Promise<TelemetryNote[]> {
      const response: AxiosResponse<ApiResponse<TelemetryNote[]>> =
        await client.get(
          `/telemetry-notes/session/${sessionId}/time-range`,
          {
            params: { start, end },
          },
        );
      return response.data.data;
    },

    async getTelemetryNoteById(id: string): Promise<TelemetryNote> {
      const response: AxiosResponse<ApiResponse<TelemetryNote>> =
        await client.get(`/telemetry-notes/${id}`);
      return response.data.data;
    },

    async createTelemetryNote(
      noteData: CreateTelemetryNoteRequest,
    ): Promise<TelemetryNote> {
      const response: AxiosResponse<ApiResponse<TelemetryNote>> =
        await client.post("/telemetry-notes", noteData);
      return response.data.data;
    },

    async updateTelemetryNote(
      id: string,
      noteData: UpdateTelemetryNoteRequest,
    ): Promise<TelemetryNote> {
      const response: AxiosResponse<ApiResponse<TelemetryNote>> =
        await client.put(`/telemetry-notes/${id}`, noteData);
      return response.data.data;
    },

    async deleteTelemetryNote(id: string): Promise<void> {
      await client.delete(`/telemetry-notes/${id}`);
    },
  };
}
